import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Trash2, MessageCircle, Sparkles, Clock, MessageSquare } from "lucide-react";
import type { ConversationSummary, Character } from "../services/api";
import { getConversations, deleteConversation, getCharacters } from "../services/api";

interface Props {
  onSelectConversation: (conv: ConversationSummary, char: Character | undefined) => void;
}

function formatFullTime(iso: string) {
  const d = new Date(iso);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${month}月${day}日 ${hours}:${minutes}`;
}

type TimeGroup = "今天" | "昨天" | "更早";

function getTimeGroup(iso: string): TimeGroup {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDay = Math.floor(diffMs / 86400000);
  if (d.toDateString() === now.toDateString()) return "今天";
  if (diffDay <= 1) return "昨天";
  return "更早";
}

export function ConversationHistory({ onSelectConversation }: Props) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [allConversations, setAllConversations] = useState<ConversationSummary[]>([]);
  const [characters, setCharacters] = useState<Record<string, Character>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [convs, chars] = await Promise.all([getConversations(), getCharacters({ nsfw: true })]);
      // 去重：每个角色只保留最新的一条对话
      const deduped = convs.reduce<ConversationSummary[]>((acc, conv) => {
        const existIdx = acc.findIndex(c => c.character_id === conv.character_id);
        if (existIdx === -1) {
          acc.push(conv);
        } else if (new Date(conv.updated_at) > new Date(acc[existIdx].updated_at)) {
          acc[existIdx] = conv;
        }
        return acc;
      }, []);
      // 按时间降序排列
      deduped.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
      setConversations(deduped);
      setAllConversations(deduped);
      const charMap: Record<string, Character> = {};
      chars.forEach((c) => (charMap[c.id] = c));
      setCharacters(charMap);
    } catch {}
    setLoading(false);
  }

  async function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } catch {}
  }

  // Group conversations by time
  const grouped = conversations.reduce<Record<TimeGroup, ConversationSummary[]>>(
    (acc, conv) => {
      const group = getTimeGroup(conv.updated_at);
      if (!acc[group]) acc[group] = [];
      acc[group].push(conv);
      return acc;
    },
    { "今天": [], "昨天": [], "更早": [] }
  );

  const groupOrder: TimeGroup[] = ["今天", "昨天", "更早"];

  return (
    <div className="h-full flex flex-col bg-slate-950">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-rose-400" />
          消息
        </h1>
        <p className="text-xs text-slate-400 mt-1">最近沟通的角色，按时间排序</p>
      </div>

      {/* Search bar */}
      <div className="px-6 pb-4 flex-shrink-0">
        <div className="relative">
          <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="搜索角色..."
            className="w-full bg-slate-900/50 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30 transition-all"
            onChange={(e) => {
              const q = e.target.value.toLowerCase();
              if (!q) {
                setConversations(allConversations);
              } else {
                setConversations(
                  allConversations.filter(
                    (c) =>
                      c.character_name.toLowerCase().includes(q) ||
                      c.last_message.toLowerCase().includes(q)
                  )
                );
              }
            }}
          />
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <motion.div key={i} animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.15 }} className="w-2 h-2 rounded-full bg-rose-400" />
              ))}
            </div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-slate-500">
            <Sparkles className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">还没有消息记录</p>
            <p className="text-xs mt-1">去角色大厅找一个角色开始聊天吧</p>
          </div>
        ) : (
          groupOrder.map((group) => {
            const items = grouped[group];
            if (items.length === 0) return null;
            return (
              <div key={group} className="mb-4">
                {/* Time group header */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{group}</span>
                  <div className="flex-1 h-px bg-slate-800/50" />
                </div>
                {/* Conversation items */}
                <div className="space-y-1.5">
                  {items.map((conv, index) => {
                    const char = characters[conv.character_id];
                    return (
                      <motion.div
                        key={conv.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        onClick={() => onSelectConversation(conv, char!)}
                        className="group flex items-center gap-3 p-3 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-rose-500/30 hover:bg-slate-900/80 cursor-pointer transition-all"
                      >
                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          <div className="w-11 h-11 rounded-full overflow-hidden bg-slate-800 ring-2 ring-slate-700 group-hover:ring-rose-500/30 transition-all">
                            {char?.avatar ? (
                              <img src={char.avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">?</div>
                            )}
                          </div>
                          {/* Online dot */}
                          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-slate-900" />
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white truncate">{conv.character_name || "未知角色"}</span>
                            <span className="text-[10px] text-slate-500 flex items-center gap-1 flex-shrink-0">
                              <Clock className="w-2.5 h-2.5" />
                              {formatFullTime(conv.updated_at)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 truncate mt-0.5">{conv.last_message || "空对话"}</p>
                        </div>
                        {/* Unread-style badge */}
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-[10px] text-slate-600 bg-slate-800/80 px-1.5 py-0.5 rounded-full">
                            {conv.message_count} 条
                          </span>
                          <button
                            onClick={(e) => handleDelete(conv.id, e)}
                            className="p-1 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                            title="删除对话"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
